
import { createContext, useCallback, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { Language, Translation, FileData, HeroState, QuoteInitialData, QuoteModalOpenOptions, QuoteTrackingContext, Tier, SpecialismDomain, Service, TierData, Specialism, QuoteFlowConfig } from '../types';
import { UI_LANGUAGES } from '../constants';
import { isFileSupported, getFileType, MAX_FILE_SIZE, MAX_FILES_HOME, MAX_FILES_QUOTE } from '../pricing';
import { createQuoteTrackingContext, getQuoteTrackingEventData } from '../lib/quoteTracking';
import { MANAGED_PUBLIC_ASSET_SETTINGS } from '../lib/managedMedia';
import { DEFAULT_QUOTE_FLOW_CONFIG, normalizeQuoteFlowConfig } from '../../shared/quoteFlow';

interface DataLoadError {
    services?: string;
    tiers?: string;
    languages?: string;
    deliveryOptions?: string;
}

interface QuoteCatalogBaseData {
    services: Service[];
    tiers: TierData[];
    deliveryOptions: any[];
    specialisms: Specialism[];
    quoteFlowConfig: QuoteFlowConfig;
    languagePricing: Array<{
        code: string;
        price_per_word: number;
        price_per_page?: number;
        price_per_minute?: number;
        minimum_word_price?: number;
        minimum_minute_price?: number;
    }>;
}

interface QuoteCatalogTranslationData {
    specialisms: Array<{ key: string; value: string }>;
    deliveryOptions: Array<{ key: string; value: string }>;
}

interface SharedBootstrapData {
    languages: Array<{
        code: string;
        name: string;
        english_name?: string;
        is_ui_lang?: boolean;
        is_active?: boolean;
    }>;
    settings: Array<{ key: string; value: string }>;
}

interface AppContextType {
    activeLang: Language;
    t: Translation | null;
    isLoading: boolean;
    dataLoadError: DataLoadError | null;
    files: FileData[];
    uploadError: { type: 'size' | 'type' | 'count' | 'totalSize' | 'extraction'; fileName?: string } | null;
    heroState: HeroState;
    isQuoteModalOpen: boolean;
    quoteInitialData: QuoteInitialData | null;
    services: Service[];
    tiers: TierData[];
    specialisms: Specialism[];
    availableLanguages: Language[]; 
    uiLanguages: Language[]; 
    settings: Record<string, string>; 
    deliveryOptions: Record<string, any>;
    refreshSettings: () => Promise<void>;
    ensureSettingsLoaded: (keys?: string[]) => Promise<void>;
    ensureQuoteCatalogLoaded: () => Promise<void>;
    isQuoteCatalogLoading: boolean;
    quoteFlowConfig: QuoteFlowConfig;


    handleLangChange: (lang: Language) => void;
    handleFilesAdded: (newFiles: FileList | null, options?: { skipHomeFileLimit?: boolean }) => Promise<void>;
    removeFile: (id: string) => void;
    removeFiles: (ids: string[]) => void;
    clearFiles: () => void;
    updateFileLanguage: (id: string, field: 'sourceLang' | 'targetLang', value: string) => void;
    toggleFileService: (fileId: string, serviceId: string) => void;
    updateFileTier: (fileId: string, tier: Tier) => void;
    updateFileSpecialism: (fileId: string, specialism: SpecialismDomain) => void;
    setHeroState: (state: HeroState) => void;
    quoteTrackingContext: QuoteTrackingContext | null;
    openQuoteModal: (options?: QuoteModalOpenOptions | boolean) => void;
    closeQuoteModal: () => void;
    onInitialDataProcessed: () => void;
    updateFileOCRStatus: (fileId: string, status: 'pending' | 'analyzing' | 'completed' | 'failed', wordCount?: number, extractedText?: string) => void;
    updateFileAnalysisResult: (
        fileId: string,
        result: {
            status: 'pending' | 'analyzing' | 'completed' | 'failed';
            wordCount?: number;
            durationMinutes?: number;
            pageCount?: number;
            requiresCustomQuote?: boolean;
            pricingReason?: string;
            extractedText?: string;
        }
    ) => void;
    updateFileStorageReference: (fileId: string, storageFileId: string, storageFileName: string, duplicateOfFileId?: string) => void;
    updateFileClassificationResult: (fileId: string, result: Partial<FileData>) => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

const LOCALES_BUCKET = 'locales';
const LOCALE_VERSION_SETTING_KEY = 'locale_artifact_version';
const SHARED_BOOTSTRAP_STORAGE_KEY = 'lm_shared_bootstrap_v2';
const SHARED_BOOTSTRAP_CACHE_TTL_MS = 30 * 60 * 1000;
const localeCache: Record<string, Translation> = {};
const localePromiseCache: Record<string, Promise<Translation> | undefined> = {};
const staticLocaleVersion = import.meta.env.VITE_LOCALE_VERSION;
let sharedBootstrapPromise: Promise<SharedBootstrapData> | null = null;
let sharedBootstrapCache: SharedBootstrapData | null = null;
let quoteCatalogBasePromise: Promise<QuoteCatalogBaseData> | null = null;
let quoteCatalogBaseCache: QuoteCatalogBaseData | null = null;
let fullSettingsPromise: Promise<Array<{ key: string; value: string }>> | null = null;
let fullSettingsCache: Array<{ key: string; value: string }> | null = null;
const settingsSubsetPromises: Record<string, Promise<Array<{ key: string; value: string }>>> = {};
const settingsCache: Record<string, string> = {};
const quoteCatalogTranslationPromises: Record<string, Promise<QuoteCatalogTranslationData>> = {};
const quoteCatalogTranslationCache: Record<string, QuoteCatalogTranslationData> = {};

const getBootstrapLanguageDefaults = (code: string) => {
    const fallback = UI_LANGUAGES.find((language) => language.code === code);
    return {
        pricePerWord: fallback?.pricePerWord ?? 0,
        pricePerMinute: fallback?.pricePerMinute ?? 0,
        minimumWordPrice: fallback?.minimumWordPrice ?? 0,
    };
};

const loadLocaleFromStorage = async (langCode: string, versionToken?: string): Promise<Translation> => {
    const effectiveVersionToken = String(versionToken || staticLocaleVersion || '').trim();
    const cacheKey = `${langCode}:${effectiveVersionToken || 'default'}`;

    if (localeCache[cacheKey]) {
        return localeCache[cacheKey];
    }

    const inFlightLocale = localePromiseCache[cacheKey];
    if (inFlightLocale) {
        return inFlightLocale;
    }

    const localePath = `${langCode.toLowerCase()}.json`;
    const { data } = supabase.storage.from(LOCALES_BUCKET).getPublicUrl(localePath);
    const localeUrl = effectiveVersionToken
        ? `${data.publicUrl}${data.publicUrl.includes('?') ? '&' : '?'}v=${encodeURIComponent(effectiveVersionToken)}`
        : data.publicUrl;
    localePromiseCache[cacheKey] = fetch(localeUrl, {
        cache: effectiveVersionToken ? 'default' : 'force-cache',
        headers: {
            'Accept': 'application/json',
        },
    })
        .then(async (response) => {
            if (!response.ok) {
                throw new Error(`Failed to load locale "${langCode}" from storage: HTTP ${response.status}`);
            }

            const text = await response.text();
            const parsed = JSON.parse(text) as Translation;
            localeCache[cacheKey] = parsed;
            return parsed;
        })
        .finally(() => {
            delete localePromiseCache[cacheKey];
        });

    return localePromiseCache[cacheKey];
};

const parseSettingsRows = (settingsData: Array<{ key: string; value: string }> | null | undefined) => {
    const settingsMap: Record<string, string> = {};
    (settingsData || []).forEach((row) => {
        settingsMap[row.key] = row.value;
    });
    return settingsMap;
};

const SHARED_LANGUAGES_SELECT = 'code,name,english_name,is_ui_lang,is_active';
const QUOTE_CATALOG_LANGUAGES_SELECT = 'code,price_per_word,price_per_page,price_per_minute,minimum_word_price,minimum_minute_price';
const SHARED_SETTINGS_SELECT = 'key,value';
const CORE_PUBLIC_SETTING_KEYS = [
    MANAGED_PUBLIC_ASSET_SETTINGS.brandPrimaryLogo,
    MANAGED_PUBLIC_ASSET_SETTINGS.siteFavicon,
    MANAGED_PUBLIC_ASSET_SETTINGS.siteAppleTouchIcon,
    MANAGED_PUBLIC_ASSET_SETTINGS.siteSocialShareImage,
    LOCALE_VERSION_SETTING_KEY,
    'quote_flow_config',
];

const mergeSettingsIntoCache = (rows: Array<{ key: string; value: string }> | null | undefined) => {
    (rows || []).forEach((row) => {
        settingsCache[row.key] = row.value;
    });
};

const getCachedSettingsRows = (keys: string[]) =>
    keys
        .filter((key) => Object.prototype.hasOwnProperty.call(settingsCache, key))
        .map((key) => ({ key, value: settingsCache[key] }));

const loadSettingsSubset = async (settingKeys: string[]): Promise<Array<{ key: string; value: string }>> => {
    const uniqueKeys = Array.from(new Set(settingKeys.filter(Boolean)));
    if (uniqueKeys.length === 0) return [];

    if (fullSettingsCache) {
        return getCachedSettingsRows(uniqueKeys);
    }

    const missingKeys = uniqueKeys.filter((key) => !Object.prototype.hasOwnProperty.call(settingsCache, key));
    if (missingKeys.length === 0) {
        return getCachedSettingsRows(uniqueKeys);
    }

    const cacheKey = missingKeys.slice().sort().join('|');
    const inFlightSettingsSubset = settingsSubsetPromises[cacheKey];
    if (typeof inFlightSettingsSubset === 'undefined') {
        settingsSubsetPromises[cacheKey] = (async () => {
            try {
                const { data, error } = await supabase
                    .from('settings')
                    .select(SHARED_SETTINGS_SELECT)
                    .in('key', missingKeys);
                if (error) throw error;
                const rows = (data || []) as Array<{ key: string; value: string }>;
                mergeSettingsIntoCache(rows);
                return rows;
            } finally {
                delete settingsSubsetPromises[cacheKey];
            }
        })();
    }

    await settingsSubsetPromises[cacheKey];
    return getCachedSettingsRows(uniqueKeys);
};

const loadAllSettingsData = async (): Promise<Array<{ key: string; value: string }>> => {
    if (fullSettingsCache) {
        return fullSettingsCache;
    }

    if (!fullSettingsPromise) {
        fullSettingsPromise = (async () => {
            try {
                const { data, error } = await supabase
                    .from('settings')
                    .select(SHARED_SETTINGS_SELECT);
                if (error) throw error;
                const rows = (data || []) as Array<{ key: string; value: string }>;
                mergeSettingsIntoCache(rows);
                fullSettingsCache = rows;
                return rows;
            } finally {
                fullSettingsPromise = null;
            }
        })();
    }

    return fullSettingsPromise ?? [];
};

const loadSharedBootstrapData = async (): Promise<SharedBootstrapData> => {
    if (sharedBootstrapCache) {
        return sharedBootstrapCache;
    }

    if (typeof window !== 'undefined') {
        try {
            const raw = window.localStorage.getItem(SHARED_BOOTSTRAP_STORAGE_KEY);
            if (raw) {
                const parsed = JSON.parse(raw) as {
                    cachedAt?: number;
                    data?: SharedBootstrapData;
                };
                if (
                    parsed.cachedAt &&
                    parsed.data &&
                    (Date.now() - parsed.cachedAt) < SHARED_BOOTSTRAP_CACHE_TTL_MS
                ) {
                    mergeSettingsIntoCache(parsed.data.settings);
                    sharedBootstrapCache = parsed.data;
                    return parsed.data;
                }
            }
        } catch (error) {
            console.warn('Failed to read shared bootstrap cache', error);
        }
    }

    if (!sharedBootstrapPromise) {
        sharedBootstrapPromise = Promise.all([
            supabase.from('languages').select(SHARED_LANGUAGES_SELECT).order('name'),
            supabase.from('settings').select(SHARED_SETTINGS_SELECT).in('key', CORE_PUBLIC_SETTING_KEYS),
        ]).then(([languagesResult, settingsResult]) => {
            if (languagesResult.error) throw languagesResult.error;
            if (settingsResult.error) throw settingsResult.error;

            const settingsRows = (settingsResult.data || []) as Array<{ key: string; value: string }>;
            mergeSettingsIntoCache(settingsRows);

            const data: SharedBootstrapData = {
                languages: languagesResult.data || [],
                settings: settingsRows,
            };
            sharedBootstrapCache = data;
            if (typeof window !== 'undefined') {
                try {
                    window.localStorage.setItem(
                        SHARED_BOOTSTRAP_STORAGE_KEY,
                        JSON.stringify({
                            cachedAt: Date.now(),
                            data,
                        }),
                    );
                } catch (error) {
                    console.warn('Failed to persist shared bootstrap cache', error);
                }
            }
            return data;
        }).finally(() => {
            sharedBootstrapPromise = null;
        });
    }

    return sharedBootstrapPromise;
};

const loadQuoteCatalogBaseData = async (): Promise<QuoteCatalogBaseData> => {
    if (quoteCatalogBaseCache) {
        return quoteCatalogBaseCache;
    }

    if (!quoteCatalogBasePromise) {
        quoteCatalogBasePromise = Promise.all([
            supabase.from('services').select('*').order('display_order', { ascending: true }),
            supabase.from('tiers').select('*').order('display_order', { ascending: true }),
            supabase.from('delivery_options').select('*').order('display_order', { ascending: true }),
            supabase.from('specialisms').select('*').eq('is_active', true).order('display_order', { ascending: true }),
            supabase.from('languages').select(QUOTE_CATALOG_LANGUAGES_SELECT).eq('is_active', true),
            supabase.from('settings').select('value').eq('key', 'quote_flow_config').maybeSingle(),
        ]).then(([servicesResult, tiersResult, deliveryResult, specialismsResult, languagesResult, quoteFlowConfigResult]) => {
            if (servicesResult.error) throw servicesResult.error;
            if (tiersResult.error) throw tiersResult.error;
            if (deliveryResult.error) throw deliveryResult.error;
            if (specialismsResult.error) throw specialismsResult.error;
            if (languagesResult.error) throw languagesResult.error;
            if (quoteFlowConfigResult.error) throw quoteFlowConfigResult.error;

            const data: QuoteCatalogBaseData = {
                services: (servicesResult.data || []) as Service[],
                tiers: (tiersResult.data || []) as TierData[],
                deliveryOptions: deliveryResult.data || [],
                specialisms: (specialismsResult.data || []) as Specialism[],
                quoteFlowConfig: normalizeQuoteFlowConfig(
                    quoteFlowConfigResult.data?.value || null,
                    DEFAULT_QUOTE_FLOW_CONFIG.classification.model,
                ),
                languagePricing: (languagesResult.data || []) as QuoteCatalogBaseData['languagePricing'],
            };
            quoteCatalogBaseCache = data;
            return data;
        }).finally(() => {
            quoteCatalogBasePromise = null;
        });
    }

    return quoteCatalogBasePromise;
};

const loadQuoteCatalogTranslations = async (langCode: string): Promise<QuoteCatalogTranslationData> => {
    if (quoteCatalogTranslationCache[langCode]) {
        return quoteCatalogTranslationCache[langCode];
    }

    const inFlightQuoteCatalogTranslations = quoteCatalogTranslationPromises[langCode];
    if (typeof inFlightQuoteCatalogTranslations === 'undefined') {
        quoteCatalogTranslationPromises[langCode] = Promise.all([
            supabase.from('translations').select('key, value').like('key', 'specialisms.%').eq('lang', langCode),
            supabase.from('translations').select('key, value').like('key', 'deliveryOptions.%').eq('lang', langCode),
        ]).then(([specialismResult, deliveryResult]) => {
            if (specialismResult.error) throw specialismResult.error;
            if (deliveryResult.error) throw deliveryResult.error;

            const data: QuoteCatalogTranslationData = {
                specialisms: (specialismResult.data || []) as Array<{ key: string; value: string }>,
                deliveryOptions: (deliveryResult.data || []) as Array<{ key: string; value: string }>,
            };
            quoteCatalogTranslationCache[langCode] = data;
            return data;
        }).finally(() => {
            delete quoteCatalogTranslationPromises[langCode];
        });
    }

    return quoteCatalogTranslationPromises[langCode];
};

export const AppProvider = ({ children }: { children: ReactNode }) => {
    const getInitialLang = (): Language => {
        const path = typeof window !== 'undefined' ? window.location.pathname : '';
        if (path.startsWith('/lt')) {
            return UI_LANGUAGES.find(l => l.code === 'LT') || UI_LANGUAGES[0];
        }
        return UI_LANGUAGES.find(l => l.code === 'EN') || UI_LANGUAGES[1];
    };
    
    const [activeLang, setActiveLang] = useState<Language>(getInitialLang);
    const [t, setT] = useState<Translation | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [translationLoadError, setTranslationLoadError] = useState<Error | null>(null);

    const [files, setFiles] = useState<FileData[]>([]);
    const [uploadError, setUploadError] = useState<{ type: 'size' | 'type' | 'count' | 'totalSize' | 'extraction'; fileName?: string } | null>(null);

    const [heroState, setHeroState] = useState<HeroState>({ source: 'LT', target: 'EN' });

    const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
    const [quoteInitialData, setQuoteInitialData] = useState<QuoteInitialData | null>(null);
    const [quoteTrackingContext, setQuoteTrackingContext] = useState<QuoteTrackingContext | null>(null);
    const [services, setServices] = useState<Service[]>([]);
    const [tiers, setTiers] = useState<TierData[]>([]);
    const [specialisms, setSpecialisms] = useState<Specialism[]>([]);
    const [availableLanguages, setAvailableLanguages] = useState<Language[]>(UI_LANGUAGES);
    const [uiLanguages, setUiLanguages] = useState<Language[]>(UI_LANGUAGES);
    const [settings, setSettings] = useState<Record<string, string>>({});
    const [deliveryOptions, setDeliveryOptions] = useState<Record<string, any>>({});
    const [quoteFlowConfig, setQuoteFlowConfig] = useState<QuoteFlowConfig>(DEFAULT_QUOTE_FLOW_CONFIG);
    const [dataLoadError, setDataLoadError] = useState<DataLoadError | null>(null);
    const [isQuoteCatalogLoading, setIsQuoteCatalogLoading] = useState(false);
    const [quoteCatalogLanguageCode, setQuoteCatalogLanguageCode] = useState<string | null>(null);
    const loadAnalytics = useCallback(() => import('../lib/analytics'), []);

    const applySettingsRows = useCallback((rows: Array<{ key: string; value: string }>, replace = false) => {
        const settingsMap = parseSettingsRows(rows);
        setSettings((prev) => (replace ? settingsMap : { ...prev, ...settingsMap }));

        if (Object.prototype.hasOwnProperty.call(settingsMap, 'delivery_options')) {
            if (settingsMap.delivery_options) {
                try {
                    const parsed = JSON.parse(settingsMap.delivery_options);
                    setDeliveryOptions(parsed);
                } catch (e) {
                    console.error('Failed to parse delivery_options from settings', e);
                }
            } else {
                setDeliveryOptions({});
            }
        }
    }, []);

    const ensureSettingsLoaded = useCallback(async (keys?: string[]) => {
        const rows = keys && keys.length > 0
            ? await loadSettingsSubset(keys)
            : await loadAllSettingsData();
        applySettingsRows(rows, !keys || keys.length === 0);
    }, [applySettingsRows]);

    const refreshSettings = useCallback(async () => {
        const rows = await loadAllSettingsData();
        applySettingsRows(rows, true);
    }, [applySettingsRows]);

    const ensureQuoteCatalogLoaded = useCallback(async () => {
        if (
            services.length > 0 &&
            tiers.length > 0 &&
            Object.keys(deliveryOptions).length > 0 &&
            specialisms.length > 0 &&
            quoteCatalogLanguageCode === activeLang.code
        ) {
            return;
        }

        setIsQuoteCatalogLoading(true);
        try {
            const currentT = localeCache[activeLang.code] || t;
            if (!currentT) {
                return;
            }

            const [baseData, translationData] = await Promise.all([
                loadQuoteCatalogBaseData(),
                loadQuoteCatalogTranslations(activeLang.code),
            ]);
            const pricingByCode = new Map(
                baseData.languagePricing.map((language) => [language.code, language]),
            );

            const rawT: any = currentT;
            const hydratedServices = baseData.services.map((service) => ({
                ...service,
                name: rawT['service']?.[service.id]?.['name'] || service.name || service.id,
                description: rawT['service']?.[service.id]?.['desc'] || service.description || '',
                slug: rawT['service']?.[service.id]?.['slug'] || service.slug || '',
            }));

            const hydratedTiers = baseData.tiers.map((tier) => ({
                ...tier,
                name: rawT['tiers']?.[tier.id]?.['label'] || tier.name || tier.id,
                description: rawT['tiers']?.[tier.id]?.['desc'] || tier.description || '',
                short_description: rawT['tiers']?.[tier.id]?.['short_desc'] || tier.short_description || '',
            }));

            const specialismTranslationMap: Record<string, string> = {};
            translationData.specialisms.forEach((entry) => {
                specialismTranslationMap[entry.key.replace('specialisms.', '')] = entry.value;
            });

            const hydratedSpecialisms = baseData.specialisms.map((specialism: any) => ({
                ...specialism,
                name: specialismTranslationMap[specialism.id] || specialism.name || specialism.id.charAt(0).toUpperCase() + specialism.id.slice(1).replace(/_/g, ' '),
            }));

            const deliveryLabelMap: Record<string, string> = {};
            const deliveryDescMap: Record<string, string> = {};
            translationData.deliveryOptions.forEach((entry) => {
                const keyPart = entry.key.replace('deliveryOptions.', '');
                if (keyPart.endsWith('.desc')) {
                    deliveryDescMap[keyPart.replace('.desc', '')] = entry.value;
                } else if (keyPart.endsWith('.label')) {
                    deliveryLabelMap[keyPart.replace('.label', '')] = entry.value;
                } else {
                    deliveryLabelMap[keyPart] = entry.value;
                }
            });

            const deliveryMap: Record<string, any> = {};
            baseData.deliveryOptions.forEach((option: any) => {
                deliveryMap[option.key] = {
                    id: option.id,
                    key: option.key,
                    label: deliveryLabelMap[option.key] || option.label,
                    description: deliveryDescMap[option.key] || option.description || '',
                    price: option.price,
                    time_estimate: option.time_estimate,
                    display_order: option.display_order,
                    is_active: option.is_active,
                    min_hours: option.min_hours,
                    max_hours: option.max_hours,
                    extra_fee: option.extra_fee,
                };
            });

            setServices(hydratedServices);
            setTiers(hydratedTiers);
            setSpecialisms(hydratedSpecialisms);
            setDeliveryOptions(deliveryMap);
            setQuoteFlowConfig(baseData.quoteFlowConfig);
            setAvailableLanguages((prevLanguages) =>
                prevLanguages.map((language) => {
                    const pricing = pricingByCode.get(language.code);
                    if (!pricing) return language;
                    return {
                        ...language,
                        pricePerWord: pricing.price_per_word,
                        pricePerPage: Number(pricing.price_per_page || 0),
                        pricePerMinute: pricing.price_per_minute || 0,
                        minimumWordPrice: pricing.minimum_word_price || 0,
                        minimumMinutePrice: pricing.minimum_minute_price || 0,
                    };
                }),
            );
            setQuoteCatalogLanguageCode(activeLang.code);
            setDataLoadError((prev) => ({
                ...prev,
                services: undefined,
                tiers: undefined,
                deliveryOptions: undefined,
            }));
        } catch (error) {
            console.error('Failed to load quote catalog data', error);
            const errorMsg = error instanceof Error ? error.message : String(error);
            setDataLoadError((prev) => ({
                ...prev,
                services: errorMsg,
                tiers: errorMsg,
                deliveryOptions: errorMsg,
            }));
        } finally {
            setIsQuoteCatalogLoading(false);
        }
    }, [activeLang.code, deliveryOptions, quoteCatalogLanguageCode, services.length, specialisms.length, t, tiers.length]);

    useEffect(() => {
        const loadData = async () => {
            setIsLoading(true);
            setTranslationLoadError(null);
            try {
                const bootstrapData = await loadSharedBootstrapData();
                const bootstrapSettings = parseSettingsRows(bootstrapData.settings);
                const currentT = await loadLocaleFromStorage(
                    activeLang.code,
                    bootstrapSettings[LOCALE_VERSION_SETTING_KEY],
                );
                
                setT(currentT);
                
                // Essential content (translations) is now loaded - show the UI
                // Backend data will continue loading in the background
                setIsLoading(false);

                if (bootstrapData.languages.length > 0) {
                    const activeServiceLangs = bootstrapData.languages.filter((language) => language.is_active);
                    setAvailableLanguages(activeServiceLangs.map(l => ({
                        ...getBootstrapLanguageDefaults(l.code),
                        code: l.code,
                        name: l.name,
                        english_name: l.english_name,
                        isUiLang: l.is_ui_lang
                    })));

                    const staticUiLangs = bootstrapData.languages.filter((language) => ['EN', 'LT'].includes(language.code));
                    setUiLanguages(staticUiLangs.map(l => ({
                        ...getBootstrapLanguageDefaults(l.code),
                        code: l.code,
                        name: l.name,
                        english_name: l.english_name,
                        isUiLang: true,
                    })));
                } else {
                    const errorMsg = 'No data';
                    console.error('Failed to load languages', errorMsg);
                    setDataLoadError(prev => ({ ...prev, languages: errorMsg }));
                }

                applySettingsRows(bootstrapData.settings, true);

            } catch (error) {
                console.error("Failed to load data", error);
                setT(null);
                setTranslationLoadError(error instanceof Error ? error : new Error(String(error)));
            } finally {
                setIsLoading(false);
            }
        };

        void loadData();
    }, [activeLang.code, applySettingsRows]);

    if (translationLoadError) {
        throw translationLoadError;
    }



    const handleLangChange = useCallback((lang: Language) => {
        setActiveLang(lang);
    }, []);

    const handleFilesAdded = async (newFiles: FileList | null, options?: { skipHomeFileLimit?: boolean }) => {
        if (!newFiles) return;
        setUploadError(null);

        let filesArray = Array.from(newFiles);

        // The single-file limit only applies to the homepage hero upload. Modals that manage
        // their own open state (e.g. the admin quote modal) pass skipHomeFileLimit.
        const isModalUpload = isQuoteModalOpen || options?.skipHomeFileLimit;
        const maxFiles = isModalUpload ? MAX_FILES_QUOTE : MAX_FILES_HOME;
        if (files.length + filesArray.length > maxFiles) {
            setUploadError({ type: 'count' });
            return;
        }

        for (const f of filesArray) {
            if (!isFileSupported(f)) {
                setUploadError({ type: 'type', fileName: f.name });
                return;
            }
            if (f.size > MAX_FILE_SIZE) {
                setUploadError({ type: 'size', fileName: f.name });
                return;
            }
        }

        const defaultSource = heroState.source || 'LT';
        const defaultTarget = heroState.target || 'EN';
        const getSignature = (file: File) => `${file.name}::${file.size}::${file.lastModified}::${file.type}`;
        const knownFiles = [...files];

        const acceptedFiles = filesArray.map((f) => {
            const fileId = Math.random().toString(36).substr(2, 9);
            const fileType = getFileType(f);

            const mandatoryServices: string[] = [];
            const initialServices: string[] = [];

            const mandatoryForType = services
                .filter(s => s.is_mandatory && s.category.includes(fileType as any))
                .map(s => s.id);

            mandatoryServices.push(...mandatoryForType);
            initialServices.push(...mandatoryForType);

            const duplicateMatch = knownFiles.find(existing => getSignature(existing.file) === getSignature(f));

            const placeholder: FileData = {
                file: f,
                type: fileType,
                id: fileId,
                wordCountEstimate: duplicateMatch?.wordCountEstimate ?? 0,
                durationMinutes: duplicateMatch?.durationMinutes ?? 0,
                pageEstimate: duplicateMatch?.pageCount ?? duplicateMatch?.pageEstimate ?? 1,
                pageCount: duplicateMatch?.pageCount ?? duplicateMatch?.pageEstimate ?? 1,
                requiresCustomQuote: duplicateMatch?.requiresCustomQuote ?? false,
                pricingReason: duplicateMatch?.pricingReason,
                services: initialServices,
                mandatoryServices: mandatoryServices,
                sourceLang: duplicateMatch?.sourceLang || defaultSource,
                sourceLangManuallySet: duplicateMatch?.sourceLangManuallySet ?? false,
                targetLang: defaultTarget,
                tier: 'professional' as Tier,
                extractedText: duplicateMatch?.extractedText,
                ocrStatus: fileType === 'image'
                    ? (duplicateMatch?.ocrStatus ?? 'pending')
                    : duplicateMatch?.ocrStatus,
                analysisStatus: duplicateMatch?.analysisStatus ?? (
                    duplicateMatch && (
                        duplicateMatch.wordCountEstimate > 0 ||
                        duplicateMatch.durationMinutes > 0 ||
                        duplicateMatch.requiresCustomQuote ||
                        Boolean(duplicateMatch.pricingReason)
                    ) ? 'completed' : 'pending'
                ),
                classificationStatus: (fileType === 'audio' || fileType === 'video') ? undefined : (duplicateMatch?.classificationStatus ?? 'pending'),
                classificationFailureReason: duplicateMatch?.classificationFailureReason,
                classificationConfidence: duplicateMatch?.classificationConfidence,
                classificationReasoning: duplicateMatch?.classificationReasoning,
                classificationOverridden: duplicateMatch?.classificationOverridden ?? false,
                classificationOverriddenCategoryId: duplicateMatch?.classificationOverriddenCategoryId,
                detectedLanguage: duplicateMatch?.detectedLanguage,
                documentCategoryId: duplicateMatch?.documentCategoryId,
                documentCategoryName: duplicateMatch?.documentCategoryName,
                flatFee: duplicateMatch?.flatFee,
                requiredTier: duplicateMatch?.requiredTier,
                requiredSpecialism: duplicateMatch?.requiredSpecialism,
                classifiedMandatoryServiceIds: duplicateMatch?.classifiedMandatoryServiceIds ?? [],
                classifiedSuggestedServices: duplicateMatch?.classifiedSuggestedServices ?? [],
                storageFileId: fileId,
                storageFileName: f.name,
                duplicateOfFileId: undefined
            };

            knownFiles.push(placeholder);
            return placeholder;
        });

        setFiles(prev => [...prev, ...acceptedFiles]);
        void loadAnalytics().then(({ trackEvent }) => trackEvent('quote_file_upload_confirmed', {
            ...(isQuoteModalOpen
                ? getQuoteTrackingEventData(quoteTrackingContext)
                : getQuoteTrackingEventData(
                    createQuoteTrackingContext({
                        entrypoint: 'hero_upload',
                        surface: 'hero_upload',
                        quoteMode: 'homepage_upload',
                        isCta: false,
                    })
                )),
            file_count: acceptedFiles.length,
        }));
    };

    const removeFile = (id: string) => {
        const newFiles = files.filter(f => f.id !== id);
        setFiles(newFiles);

        if (newFiles.length > 0) {
            setHeroState({
                source: newFiles[0].sourceLang,
                target: newFiles[0].targetLang
            });
        }
    };

    const removeFiles = (ids: string[]) => {
        const newFiles = files.filter(f => !ids.includes(f.id));
        setFiles(newFiles);

        if (newFiles.length > 0) {
            setHeroState({
                source: newFiles[0].sourceLang,
                target: newFiles[0].targetLang
            });
        }
    };

    const clearFiles = useCallback(() => {
        setFiles([]);
        setHeroState({ source: 'LT', target: 'EN' }); 
    }, []);

    const updateFileLanguage = (id: string, field: 'sourceLang' | 'targetLang', value: string) => {
        setFiles(prevFiles => {
            const sourceFile = prevFiles.find(f => f.id === id);
            const sourceStorageFileId = sourceFile?.storageFileId || sourceFile?.id;
            const sourceStorageFileName = sourceFile?.storageFileName || sourceFile?.file.name;
            const newFiles = prevFiles.map(f => {
                const samePhysicalFile = Boolean(
                    field === 'sourceLang' &&
                    sourceStorageFileId &&
                    sourceStorageFileName &&
                    (f.storageFileId || f.id) === sourceStorageFileId &&
                    (f.storageFileName || f.file.name) === sourceStorageFileName
                );
                const shouldUpdate = f.id === id || samePhysicalFile;
                if (!shouldUpdate) return f;

                return {
                    ...f,
                    [field]: value,
                    ...(field === 'sourceLang' ? { sourceLangManuallySet: true } : {}),
                };
            });

            if (newFiles.length === 1 && newFiles[0].id === id) {
                if (field === 'sourceLang') {
                    setHeroState(prev => ({ ...prev, source: value }));
                } else {
                    setHeroState(prev => ({ ...prev, target: value }));
                }
            }

            return newFiles;
        });
        void loadAnalytics().then(({ trackQuoteLanguageSelect }) =>
            trackQuoteLanguageSelect(quoteTrackingContext, { file_id: id, field, language_code: value })
        );
    };

    const toggleFileService = (fileId: string, serviceId: string) => {
        setFiles(files.map(f => {
            if (f.id === fileId) {
                const hasService = f.services.includes(serviceId);
                void loadAnalytics().then(({ trackEvent }) => trackEvent('quote_service_select', {
                    file_id: fileId,
                    service_id: serviceId,
                    selected: !hasService,
                }));
                return {
                    ...f,
                    services: hasService ? f.services.filter(s => s !== serviceId) : [...f.services, serviceId]
                };
            }
            return f;
        }));
    };

    const updateFileTier = (fileId: string, tier: Tier) => {
        void loadAnalytics().then(({ trackEvent }) => trackEvent('quote_tier_select', {
            file_id: fileId,
            tier_id: tier,
        }));
        setFiles(files.map(f => {
            if (f.id === fileId) {
                let updatedServices = f.services;

                if (tier === 'machine' && f.type === 'text') {
                    updatedServices = f.services.filter(s => s === 'edit');
                }

                return {
                    ...f,
                    tier,
                    services: updatedServices,
                    specialism: tier === 'specialist' ? f.specialism : undefined
                };
            }
            return f;
        }));
    };

    const updateFileSpecialism = (fileId: string, specialism: SpecialismDomain) => {
        void loadAnalytics().then(({ trackQuoteSpecialismSelect }) =>
            trackQuoteSpecialismSelect(quoteTrackingContext, { file_id: fileId, specialism_id: specialism })
        );
        setFiles(files.map(f => {
            if (f.id === fileId) {
                return { ...f, specialism };
            }
            return f;
        }));
    };

    const handleHeroStateChange = (newState: HeroState) => {
        setHeroState(newState);

        if (files.length === 1) {
            setFiles(prevFiles => prevFiles.map(f => ({
                ...f,
                sourceLang: newState.source,
                targetLang: newState.target
            })));
        }
    };

    const openQuoteModal = useCallback((options: QuoteModalOpenOptions | boolean = false) => {
        const skipServiceSelection = typeof options === 'boolean' ? options : options.skipServiceSelection;
        const showContact = typeof options === 'object' ? options.showContact : false;
        const incomingTracking =
            typeof options === 'object' && options.tracking
                ? options.tracking
                : createQuoteTrackingContext({
                    entrypoint: typeof options === 'boolean' && options ? 'prefilled_quote' : 'main_layout_quote',
                    surface: 'quote_modal',
                    quoteMode: showContact ? 'contact' : skipServiceSelection ? 'skip_service_selection' : 'standard',
                    isCta: false,
                });

        const hasActiveSession = quoteTrackingContext !== null && files.length > 0;
        if (hasActiveSession) {
            setIsQuoteModalOpen(true);
            return;
        }

        void ensureQuoteCatalogLoaded().then(() => {
            setQuoteInitialData({
                source: heroState.source,
                target: heroState.target,
                skipServiceSelection,
                showContact,
                tracking: incomingTracking,
            });
            setQuoteTrackingContext(incomingTracking);
            void loadAnalytics().then(({ trackQuoteModalOpen }) => trackQuoteModalOpen(incomingTracking, {
                has_files: files.length > 0,
                file_count: files.length,
            }));
            setIsQuoteModalOpen(true);
        });
    }, [ensureQuoteCatalogLoaded, files.length, heroState.source, heroState.target, loadAnalytics, quoteTrackingContext]);

    const closeQuoteModal = useCallback(() => {
        setIsQuoteModalOpen(false);
    }, []);

    const onInitialDataProcessed = useCallback(() => {
        setQuoteInitialData(null);
    }, []);

    const updateFileOCRStatus = (fileId: string, status: 'pending' | 'analyzing' | 'completed' | 'failed', wordCount?: number, extractedText?: string) => {
        setFiles(prevFiles => {
            const sourceFile = prevFiles.find(f => f.id === fileId);
            const sourceStorageFileId = sourceFile?.storageFileId || sourceFile?.id;
            const sourceStorageFileName = sourceFile?.storageFileName || sourceFile?.file.name;

            return prevFiles.map(f => {
                const samePhysicalFile = Boolean(
                    sourceStorageFileId &&
                    sourceStorageFileName &&
                    (f.storageFileId || f.id) === sourceStorageFileId &&
                    (f.storageFileName || f.file.name) === sourceStorageFileName
                );
                const shouldMirror = f.id === fileId || f.duplicateOfFileId === fileId || samePhysicalFile;
                if (!shouldMirror) return f;

                const updates: Partial<FileData> = { ocrStatus: status, analysisStatus: status };
                if (status === 'completed' && wordCount !== undefined) {
                    updates.wordCountEstimate = wordCount;
                    updates.extractedText = extractedText;
                    updates.requiresCustomQuote = false;
                    updates.pricingReason = undefined;
                } else if (status === 'failed') {
                    updates.requiresCustomQuote = true;
                    updates.pricingReason = 'ocr_failed';
                }
                return { ...f, ...updates };
            });
        });
    };

    const updateFileAnalysisResult = (
        fileId: string,
        result: {
            status: 'pending' | 'analyzing' | 'completed' | 'failed';
            wordCount?: number;
            durationMinutes?: number;
            pageCount?: number;
            requiresCustomQuote?: boolean;
            pricingReason?: string;
            extractedText?: string;
        }
    ) => {
        setFiles(prevFiles => {
            const sourceFile = prevFiles.find(f => f.id === fileId);
            const sourceStorageFileId = sourceFile?.storageFileId || sourceFile?.id;
            const sourceStorageFileName = sourceFile?.storageFileName || sourceFile?.file.name;

            return prevFiles.map(f => {
                const samePhysicalFile = Boolean(
                    sourceStorageFileId &&
                    sourceStorageFileName &&
                    (f.storageFileId || f.id) === sourceStorageFileId &&
                    (f.storageFileName || f.file.name) === sourceStorageFileName
                );
                const shouldMirror = f.id === fileId || f.duplicateOfFileId === fileId || samePhysicalFile;
                if (!shouldMirror) return f;

                const updates: Partial<FileData> = {
                    analysisStatus: result.status,
                };
                if (result.wordCount !== undefined) updates.wordCountEstimate = result.wordCount;
                if (result.durationMinutes !== undefined) updates.durationMinutes = result.durationMinutes;
                if (result.extractedText !== undefined) updates.extractedText = result.extractedText;
                if (result.pageCount !== undefined) {
                    updates.pageCount = result.pageCount;
                    updates.pageEstimate = result.pageCount;
                }
                if (result.requiresCustomQuote !== undefined) updates.requiresCustomQuote = result.requiresCustomQuote;
                if (result.pricingReason !== undefined) updates.pricingReason = result.pricingReason;
                if (result.extractedText !== undefined) updates.extractedText = result.extractedText;

                return { ...f, ...updates };
            });
        });
    };

    const updateFileClassificationResult = (fileId: string, result: Partial<FileData>) => {
        setFiles(prevFiles => {
            const sourceFile = prevFiles.find(f => f.id === fileId);
            const sourceStorageFileId = sourceFile?.storageFileId || sourceFile?.id;
            const sourceStorageFileName = sourceFile?.storageFileName || sourceFile?.file.name;

            return prevFiles.map(f => {
                const samePhysicalFile = Boolean(
                    sourceStorageFileId &&
                    sourceStorageFileName &&
                    (f.storageFileId || f.id) === sourceStorageFileId &&
                    (f.storageFileName || f.file.name) === sourceStorageFileName
                );
                const shouldMirror = f.id === fileId || f.duplicateOfFileId === fileId || samePhysicalFile;
                if (!shouldMirror) return f;

                const mergedResult = { ...result };
                if (f.sourceLangManuallySet && result.sourceLang) {
                    delete mergedResult.sourceLang;
                }
                return { ...f, ...mergedResult };
            });
        });
    };

    const updateFileStorageReference = (
        fileId: string,
        storageFileId: string,
        storageFileName: string,
        duplicateOfFileId?: string
    ) => {
        setFiles(prevFiles => prevFiles.map(f => {
            if (f.id === fileId) {
                return {
                    ...f,
                    storageFileId,
                    storageFileName,
                    duplicateOfFileId
                };
            }
            return f;
        }));
    };

    return (
        <AppContext.Provider value={{
            activeLang,
            t,
            isLoading,
            dataLoadError,
            files,
            uploadError,
            heroState,
            isQuoteModalOpen,
            quoteInitialData,
            services,
            tiers,
            specialisms,
            availableLanguages,
            uiLanguages,
            settings,
            deliveryOptions,
            refreshSettings,
            ensureSettingsLoaded,
            ensureQuoteCatalogLoaded,
            isQuoteCatalogLoading,
            quoteFlowConfig,
            handleLangChange,
            handleFilesAdded,
            removeFile,
            removeFiles,
            clearFiles,
            updateFileLanguage,
            toggleFileService,
            updateFileTier,
            updateFileSpecialism,
            setHeroState: handleHeroStateChange,
            quoteTrackingContext,
            openQuoteModal,
            closeQuoteModal,
            onInitialDataProcessed,
            updateFileOCRStatus,
            updateFileAnalysisResult,
            updateFileStorageReference,
            updateFileClassificationResult
        }}>
            {children}
        </AppContext.Provider>
    );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAppContext = () => {
    const context = useContext(AppContext);
    if (context === undefined) {
        throw new Error('useAppContext must be used within an AppProvider');
    }
    return context;
};
